import { useCallback, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { ConfidenceBar } from '@/components/px/ConfidenceBar'
import { DecisionCard } from '@/components/px/DecisionCard'
import { DensityNote } from '@/components/px/DensityNote'
import { OpenTaskBanner } from '@/components/px/OpenTaskBanner'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { resourcesNav } from '@/components/px/nav'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ALLOCATION_CHROME,
  ALLOCATION_DENSITY_NOTE,
  CANDIDATE_ROWS,
  CHANGE_BOOKING,
  DECISION,
  GROWTH_PANEL,
  MATCH_COLUMNS,
  MATCH_DENSITY_LINE,
  MATCH_SECTION_LABEL,
  MATCH_TABLE_CAPTION,
  MATCH_TAG_COLUMN_LABEL,
  OPEN_TASK_BANNER,
  OPEN_TASK_PROJECT,
  OVERRIDE_RECORD,
  type Segment,
} from '@/demo/content/allocation'
import { roleByKey } from '@/demo/data'
import { useAppSelector } from '@/store'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'

/** Prose in which some words are real destinations (never a dead phrase). */
function Prose({ segments }: { segments: readonly Segment[] }) {
  return (
    <>
      {segments.map((segment, i) =>
        segment.kind === 'text' ? (
          <span key={i}>{segment.text}</span>
        ) : (
          <Link
            key={i}
            to={segment.href}
            className="font-medium text-accent-foreground underline underline-offset-2 hover:no-underline"
          >
            {segment.label}
          </Link>
        )
      )}
    </>
  )
}

/**
 * Screen 1.4 — Allocation. An open task from any project lands here, PX ranks
 * the whole group on skill × availability × rate with the trade-off written
 * out, and a named human signs off before the hours are booked.
 *
 * Colour contract (locked): aubergine = decided (the open-task banner, the
 * approved chip); mint = proposed (the match panel, the growth-move panel).
 * The top-ranked row is never tinted — the tint belongs to PX's proposal
 * furniture, not to a row selection.
 */
export default function AllocationPage() {
  const currentRole = useAppSelector(selectActiveRoleKey)
  const roleLabel = roleByKey(currentRole).label

  const [changeOpen, setChangeOpen] = useState(false)
  const [changeConfirmed, setChangeConfirmed] = useState(false)

  const onChangeBookingClicked = useCallback(() => {
    setChangeOpen((open) => !open)
    setChangeConfirmed(false)
  }, [])
  const onConfirmChangeClicked = useCallback(() => setChangeConfirmed(true), [])
  const onCancelChangeClicked = useCallback(() => {
    setChangeOpen(false)
    setChangeConfirmed(false)
  }, [])

  return (
    <AppShell
      moduleLabel={ALLOCATION_CHROME.moduleLabel}
      breadcrumb={ALLOCATION_CHROME.breadcrumb}
      contextPill={ALLOCATION_CHROME.contextPill}
      nav={resourcesNav}
    >
      <h1 className="sr-only">{ALLOCATION_CHROME.documentTitle}</h1>
      {/* PX-W6.2 (AC-8): the demo badge — this surface renders seed content and stays network-quiet. */}
      <div className="flex justify-end">
        <ConceptBadge />
      </div>

      <div className="flex flex-col gap-5">
        {/* 1 — the open task, aubergine (decided furniture) */}
        <OpenTaskBanner
          eyebrow={OPEN_TASK_BANNER.eyebrow}
          title={OPEN_TASK_BANNER.title}
          meta={OPEN_TASK_BANNER.meta}
        >
          <Link
            to={OPEN_TASK_PROJECT.href}
            className="mt-2 inline-block text-sm font-medium text-white underline underline-offset-2 hover:no-underline"
          >
            {OPEN_TASK_PROJECT.label} →
          </Link>
        </OpenTaskBanner>

        {/* 2 — what PX proposes, mint */}
        <section aria-labelledby="px-match-label" className="rounded-lg bg-accent px-4 py-4">
          <div id="px-match-label">
            <SectionLabel>{MATCH_SECTION_LABEL}</SectionLabel>
          </div>

          <div className="mt-3 overflow-hidden rounded-md border border-border bg-card">
            <Table>
              <TableCaption className="sr-only">{MATCH_TABLE_CAPTION}</TableCaption>
              <TableHeader>
                <TableRow>
                  {MATCH_COLUMNS.map((column) => (
                    <TableHead
                      key={column}
                      className="px-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                    >
                      {column}
                    </TableHead>
                  ))}
                  <TableHead className="px-3">
                    <span className="sr-only">{MATCH_TAG_COLUMN_LABEL}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {CANDIDATE_ROWS.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell className="px-3 py-2.5">
                      <span className="flex items-center gap-2.5">
                        <span
                          aria-hidden
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-people-bg text-xs font-semibold text-people-ink"
                        >
                          {row.initial}
                        </span>
                        <span className="leading-tight">
                          {row.href === null ? (
                            <span className="block font-medium text-foreground">{row.name}</span>
                          ) : (
                            <Link
                              to={row.href}
                              className="block font-medium text-foreground underline underline-offset-2 hover:no-underline"
                            >
                              {row.name}
                            </Link>
                          )}
                          <span className="block text-xs text-muted-foreground">{row.detail}</span>
                        </span>
                      </span>
                    </TableCell>
                    <TableCell className="px-3 py-2.5">
                      <ConfidenceBar pct={row.fitPct} />
                    </TableCell>
                    <TableCell className="v-figure px-3 py-2.5 text-sm tabular-nums text-foreground">
                      {row.freePct === null ? '—' : `${row.freePct}%`}
                    </TableCell>
                    <TableCell className="v-figure px-3 py-2.5 text-sm tabular-nums text-foreground">
                      {row.rateLabel}
                    </TableCell>
                    <TableCell className="whitespace-normal px-3 py-2.5 text-sm text-foreground">
                      {row.impact}
                    </TableCell>
                    <TableCell className="px-3 py-2.5 text-right">
                      <StatusPill tone={row.tone}>{row.tag}</StatusPill>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <p className="mt-2.5 text-xs text-muted-foreground">
            <Prose segments={MATCH_DENSITY_LINE} />
          </p>
        </section>

        {/* 3 — the trade-off of the growth move, mint */}
        <section aria-labelledby="growth-move-label" className="rounded-lg bg-accent px-4 py-4">
          <div id="growth-move-label">
            <SectionLabel>{GROWTH_PANEL.label}</SectionLabel>
          </div>
          <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-foreground">
            <Prose segments={GROWTH_PANEL.prose} />
          </p>
        </section>

        {/* 4 — the decision, already taken by a named human */}
        <section aria-label="Decision">
          <DecisionCard
            summary={<Prose segments={DECISION.summary} />}
            approvedBy={DECISION.approvedBy}
            approvedAt={DECISION.approvedAt}
            consequences={DECISION.consequences}
            loggedLine={DECISION.loggedLine}
            overrideHref={DECISION.overrideHref}
            primaryAction={
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-expanded={changeOpen}
                aria-controls="change-booking-panel"
                onClick={onChangeBookingClicked}
              >
                {CHANGE_BOOKING.trigger}
              </Button>
            }
          />

          {changeOpen && (
            <div
              id="change-booking-panel"
              className="mt-2 rounded-lg border border-border bg-muted px-4 py-3"
            >
              <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <TriangleAlert className="h-4 w-4 text-review" aria-hidden />
                {CHANGE_BOOKING.heading}
              </h2>
              <p className="mt-1 max-w-2xl text-sm text-foreground">{CHANGE_BOOKING.consequence}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                You are signed in as {roleLabel} — the change is recorded under that name.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" onClick={onConfirmChangeClicked}>
                  {CHANGE_BOOKING.confirm}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onCancelChangeClicked}>
                  {CHANGE_BOOKING.cancel}
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground" role="status">
                {changeConfirmed ? CHANGE_BOOKING.confirmed : ''}
              </p>
            </div>
          )}
        </section>

        {/* 5 — the record `view override reason →` lands on */}
        <section
          id={OVERRIDE_RECORD.id}
          aria-labelledby="override-record-label"
          className="scroll-mt-6 rounded-lg border border-border bg-card px-4 py-3"
        >
          <div id="override-record-label">
            <SectionLabel>{OVERRIDE_RECORD.label}</SectionLabel>
          </div>
          <dl className="mt-2 grid grid-cols-[7rem_1fr] gap-x-4 gap-y-1.5 text-sm">
            {OVERRIDE_RECORD.rows.map((row) => (
              <div key={row.term} className="col-span-2 grid grid-cols-subgrid">
                <dt className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  {row.term}
                </dt>
                <dd className="text-foreground">
                  <Prose segments={row.value} />
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-2.5 border-t border-border pt-2 text-xs text-muted-foreground">
            {OVERRIDE_RECORD.footnote}
          </p>
        </section>

        {/* 6 — what the page withholds */}
        <DensityNote>{ALLOCATION_DENSITY_NOTE}</DensityNote>
      </div>
    </AppShell>
  )
}
